import { useEffect, useState } from "react";
import type { FormEvent } from "react";
import "./ChatInput.css";
import "./Field.css";
import { Button } from "./Button";
import { tx } from "../i18n";

/* ══ 말 걸기 · 멈춤 ══
   입력칸은 **도는 세션에만** 그려진다(안 B). 죽은 입력칸은 고장으로 읽힌다. 발자국 탭(사람
   세션)에서는 멈춤 없이 입력만 선다 — 열린 세션이 없으면 다음에 집는 세션이 받는다.

   멈춤은 되돌릴 수 없다 — 두 번 누른다. 첫 누름은 글자로 「다시 누르면 멈춘다」고 말하고
   4초 뒤 되돌아간다. 보내기 실패는 칸을 비우지 않는다. */

export function ChatInput({
  hint,
  onSend,
  onStop,
  stopping,
  sending,
  disabled,
}: {
  hint: string;
  onSend: (text: string) => Promise<void>;
  /** 없으면 멈춤 단추를 안 그린다(발자국 탭). */
  onStop?: () => Promise<void>;
  /** 멈춤 표가 이미 섰다 — 단추는 「멈추는 중」으로 굳는다. */
  stopping?: boolean;
  sending?: boolean;
  disabled?: boolean;
}) {
  const [text, setText] = useState("");
  const [arm, setArm] = useState(false);
  const [stopBusy, setStopBusy] = useState(false);

  useEffect(() => {
    if (!arm) return;
    const t = setTimeout(() => setArm(false), 4000);
    return () => clearTimeout(t);
  }, [arm]);

  const submit = async (ev: FormEvent) => {
    ev.preventDefault();
    const body = text.trim();
    if (!body || sending) return;
    await onSend(body);
    // 성공했을 때만 비운다 — 실패는 onSend 가 던지고 위에서 토스트한다.
    setText("");
  };

  const stop = async () => {
    if (!onStop) return;
    if (!arm) {
      setArm(true);
      return;
    }
    setArm(false);
    setStopBusy(true);
    try {
      await onStop();
    } finally {
      setStopBusy(false);
    }
  };

  return (
    <form className="cin" onSubmit={submit}>
      <p className="cin__hint">{hint}</p>
      <div className="cin__row">
        <input
          className="b-input is-md cin__field"
          placeholder={tx("chat.input.hint")}
          value={text}
          disabled={disabled}
          aria-label={tx("chat.input.label")}
          onChange={(e) => setText(e.target.value)}
        />
        <Button size="md" variant="primary" type="submit" loading={sending} disabled={disabled || !text.trim()}>
          {tx("chat.input.send")}
        </Button>
        {onStop ? (
          <Button
            size="md"
            variant="danger"
            loading={stopBusy}
            disabled={disabled || stopping}
            title={tx(stopping ? "chat.input.stoppingTip" : "chat.input.stopTip")}
            onClick={stop}
          >
            <span aria-hidden="true">■</span> {stopping ? tx("chat.stopping") : arm ? tx("chat.input.arm") : tx("chat.stop")}
          </Button>
        ) : null}
      </div>
    </form>
  );
}
